import React, { useState, useEffect, useRef } from 'react';
import { DevToMock, LinkedInMock, RedditMock, SiteMock, OGCardMock } from './DestinationMock';

interface FormatCardProps {
  brand: string;
  slug: string;
  format: string;
  rendition: { hash?: string; renderedAt?: string; score?: number; passed?: boolean } | null;
  narrativeHash: string | null;
  destinations: { channel: string; published?: { permalink: string; publishedAt: string } }[];
  violations: { metric: string; value: number; threshold: number; severity: string }[];
  onAction: (action: string, slug: string) => void;
  brandName?: string;
  isStale?: boolean;
  fileBase?: string;
}

const FORMAT_NAMES: Record<string, string> = {
  blog: 'Blog', 'social-card': 'Social Card', 'social-text': 'Social Post',
  reddit: 'Reddit', 'social-carousel': 'Carousel', 'linkedin-text': 'LinkedIn Text',
  'og-image': 'OG Image', 'ebook-html': 'Ebook', landing: 'Landing Page', newsletter: 'Newsletter',
};

function fileExt(type: string): string {
  // Issue #120: social-carousel persists as a JSON envelope
  // ({ caption, hashtags, slides[] }), not a binary PNG. social-card and
  // og-image are the single-PNG formats. linkedin-carousel (Social Studio)
  // also writes .json.
  if (['social-card', 'og-image'].includes(type)) return '.png';
  if (['social-carousel', 'linkedin-carousel', 'x-thread', 'x-listicle-thread'].includes(type)) return '.json';
  if (['social-text', 'reddit', 'linkedin-text'].includes(type)) return '.md';
  return '.html';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function FormatCard({ brand, slug, format, rendition, narrativeHash, destinations, violations, onAction, brandName, isStale: isStaleFromParent, fileBase: fileBaseProp }: FormatCardProps) {
  const fBase = fileBaseProp || `/api/${brand}/pieces/${slug}/file`;
  const [content, setContent] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const exists = !!rendition;
  const score = rendition?.score ?? null;
  const passed = rendition?.passed ?? null;
  const name = FORMAT_NAMES[format] || format;
  // #9 fix: Staleness is passed from parent (EngineView computes it from compose/render hashes)
  const isStale = isStaleFromParent ?? false;

  // #3 fix: Track blob URLs and revoke on cleanup
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!exists) { setContent(null); return; }
    // Revoke previous blob URL before creating new one
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    const ext = fileExt(format);
    fetch(`${fBase}/renditions/${format}${ext}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return ext === '.png' ? r.blob().then(b => {
          const url = URL.createObjectURL(b);
          blobUrlRef.current = url;
          return url;
        }) : r.text();
      })
      .then(c => setContent(c as string))
      .catch(() => setContent(null));
    return () => { if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; } };
  }, [format, slug, brand, exists]);

  // ── Carousel cover image (#295) ──────────────────────────────────────────
  const isCarousel = format === 'social-carousel' || format === 'linkedin-carousel';
  const [coverHasImage, setCoverHasImage] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [assets, setAssets] = useState<{ id: string; label: string; url: string; onBackground?: string }[]>([]);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);

  useEffect(() => {
    if (!isCarousel) return;
    fetch(`/api/${brand}/social/carousel-cover?slug=${encodeURIComponent(slug)}`)
      .then(r => (r.ok ? r.json() : { hasImage: false }))
      .then(d => setCoverHasImage(!!d.hasImage))
      .catch(() => {});
  }, [isCarousel, brand, slug]);

  async function postCover(payload: { image?: string | null; assetId?: string }): Promise<void> {
    const r = await fetch(`/api/${brand}/social/carousel-cover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, ...payload }),
    });
    if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${r.status}`);
  }

  async function onCoverFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { alert('Cover must be a PNG, JPEG, or WEBP image.'); return; }
    setCoverBusy(true);
    try {
      const dataUri = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result as string);
        fr.onerror = () => rej(fr.error);
        fr.readAsDataURL(file);
      });
      await postCover({ image: dataUri });
      setCoverHasImage(true);
    } catch (err) {
      alert(`Cover upload failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCoverBusy(false);
    }
  }

  async function onCoverRemove(): Promise<void> {
    setCoverBusy(true);
    try {
      await postCover({ image: null });
      setCoverHasImage(false);
    } catch (err) {
      alert(`Remove failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCoverBusy(false);
    }
  }

  async function toggleAssetPicker(): Promise<void> {
    if (!assetPickerOpen && assets.length === 0) {
      try {
        const r = await fetch(`/api/${brand}/assets`);
        if (!r.ok) throw new Error(`assets fetch failed: ${r.status}`);
        const d = (await r.json()) as { assets?: typeof assets };
        setAssets(d.assets ?? []);
      } catch { /* leave empty; picker shows "No brand assets found." */ }
    }
    setAssetPickerOpen(v => !v);
  }

  async function pickAsset(assetId: string): Promise<void> {
    setCoverBusy(true);
    try {
      await postCover({ assetId });
      setCoverHasImage(true);
      setAssetPickerOpen(false);
    } catch (err) {
      alert(`Apply failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCoverBusy(false);
    }
  }

  // Not generated
  if (!exists) {
    return (
      <div className="format-card format-card--empty" role="button" tabIndex={0} aria-label={`Generate ${name}`} onClick={() => onAction(`render:${format}`, slug)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAction(`render:${format}`, slug); } }}>
        <div className="format-card__header">
          <span className="format-card__name">{name}</span>
          <span className="format-card__badge format-card__badge--empty">{'\u25CB'} Not generated</span>
        </div>
        <div className="format-card__empty-body">
          <div style={{ color: 'var(--text-tertiary)', fontSize: 13, marginBottom: 8 }}>Click to generate this format.</div>
          <button className="btn btn-secondary btn--sm">Generate</button>
        </div>
      </div>
    );
  }

  // Extract title from HTML content
  let title = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  let bodyHtml = content || '';
  if (content && fileExt(format) === '.html') {
    const titleMatch = content.match(/<h1[^>]*>([\s\S]*?)<\/h1>/);
    if (titleMatch) title = titleMatch[1].replace(/<[^>]+>/g, '').trim().split('\n')[0];
    const bodyMatch = content.match(/<div class="article-body">([\s\S]*?)<\/div>\s*<\/article>/);
    if (bodyMatch) bodyHtml = bodyMatch[1];
  }

  // Render destination-native mockup
  function renderMockup() {
    if (!content) return null;

    // Determine primary destination for this format
    const primaryDest = destinations.length > 0 ? destinations[0].channel : 'site';

    if (format === 'blog' && primaryDest === 'dev-to') {
      return <DevToMock title={title} bodyHtml={bodyHtml} tags={['platform', 'engineering', 'devops']} />;
    }
    if (format === 'blog') {
      return <SiteMock title={title} bodyHtml={bodyHtml} readingTime="8 min read" />;
    }
    if (format === 'linkedin-text' || format === 'social-text') {
      return <LinkedInMock text={content} brandName={brandName} />;
    }
    if (format === 'social-card' || format === 'og-image') {
      return <OGCardMock imageUrl={content} title={title} domain={`${brand}.com`} />;
    }
    if (format === 'reddit') {
      return <RedditMock title={title} bodyMd={content} subreddit="programming" />;
    }
    if (format === 'social-carousel') {
      return <OGCardMock imageUrl={content} title="Carousel" />;
    }
    // Fallback
    if (fileExt(format) === '.png') {
      return <img src={content} alt={name} style={{ maxWidth: '100%', borderRadius: 0 }} />;
    }
    return <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)', maxHeight: 200, overflow: 'hidden' }}>{content.slice(0, 500)}</pre>;
  }

  return (
    <div className={`format-card ${isStale ? 'format-card--stale' : ''} ${expanded ? 'format-card--expanded' : ''}`} onClick={() => !expanded && setExpanded(true)}>
      {/* Header */}
      <div className="format-card__header">
        <span className="format-card__name">{name}</span>
        {score !== null && (
          <span className={`format-card__badge ${score >= 80 ? 'format-card__badge--good' : score >= 60 ? 'format-card__badge--ok' : 'format-card__badge--bad'}`}>
            {passed ? '\u2713' : '\u2717'} {score}
          </span>
        )}
        {expanded && <button className="format-card__close" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>{'\u2715'}</button>}
      </div>

      {/* Lineage */}
      <div className="format-card__lineage">
        From narrative <span className="mono">{narrativeHash?.slice(0, 8)}...</span>
        {rendition?.renderedAt && <span> {'\u2022'} {timeAgo(rendition.renderedAt)}</span>}
        {isStale && <span className="format-card__stale-badge">{'\u26A0'} Stale — <button className="format-card__fix-btn" onClick={(e) => { e.stopPropagation(); onAction(`render:${format}`, slug); }}>Re-render</button></span>}
        {/* Always-visible Regenerate — issue #117. Stale state shows a louder
            "Re-render" CTA, but a fresh rendition also needs a way back to the
            renderer (e.g. to tune the visual + re-run the eval). */}
        {!isStale && (
          <button
            type="button"
            data-testid={`regenerate-${format}`}
            className="format-card__fix-btn"
            onClick={(e) => { e.stopPropagation(); onAction(`render:${format}`, slug); }}
            style={{ marginLeft: 8 }}
            title="Re-run the renderer for this format"
          >
            Regenerate
          </button>
        )}
      </div>

      {/* Carousel cover image upload (#295) — optional product visual on slide 1 */}
      {isCarousel && (
        <div
          className="format-card__cover-upload"
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '2px 0 6px', fontSize: 12, color: 'var(--text-secondary)' }}
        >
          <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onCoverFile} />
          <span title="Optional product screenshot shown on the carousel cover slide. Off by default.">
            Cover image: {coverHasImage ? '✓ uploaded' : 'none'}
          </span>
          <button type="button" className="format-card__fix-btn" disabled={coverBusy} onClick={() => coverInputRef.current?.click()}>
            {coverBusy ? '…' : coverHasImage ? 'Replace' : 'Upload'}
          </button>
          <button type="button" className="format-card__fix-btn" disabled={coverBusy} onClick={toggleAssetPicker} title="Apply a brand asset from the library">
            Brand asset
          </button>
          {coverHasImage && (
            <button type="button" className="format-card__fix-btn" disabled={coverBusy} onClick={onCoverRemove}>Remove</button>
          )}
          {coverHasImage && <span style={{ color: 'var(--text-tertiary)' }}>{'→'} Regenerate to apply</span>}
          {assetPickerOpen && (
            <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
              {assets.length === 0 && <span style={{ color: 'var(--text-tertiary)' }}>No brand assets found.</span>}
              {assets.map(a => (
                <button
                  key={a.id}
                  type="button"
                  disabled={coverBusy}
                  onClick={() => pickAsset(a.id)}
                  title={a.label}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 6, border: '1px solid var(--border, #d9d3bf)', background: a.onBackground === 'dark' ? '#141414' : '#faf7ec', cursor: 'pointer' }}
                >
                  <img src={a.url} alt={a.label} style={{ width: 56, height: 56, objectFit: 'contain' }} />
                  <span style={{ fontSize: 10, maxWidth: 72, textAlign: 'center', color: a.onBackground === 'dark' ? '#faf7ec' : 'var(--text-secondary)' }}>{a.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Destination-native preview */}
      <div className="format-card__mockup" style={!expanded ? { maxHeight: 220, overflow: 'hidden', maskImage: 'linear-gradient(to bottom, black 70%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent)' } : undefined}>
        {renderMockup()}
      </div>
      {!expanded && content && <div className="format-card__click-hint">Click to expand</div>}

      {/* Quality gates (shown when expanded) */}
      {expanded && violations.length > 0 && (
        <div className="format-card__gates">
          <div className="format-card__gates-title">Quality Gates</div>
          {violations.map((v, i) => (
            <div key={i} className={`format-card__gate ${v.severity === 'error' ? 'format-card__gate--fail' : v.severity === 'warning' ? 'format-card__gate--warn' : 'format-card__gate--pass'}`}>
              <span className="format-card__gate-icon">{v.severity === 'error' ? '\u2717' : v.severity === 'warning' ? '\u26A0' : '\u2713'}</span>
              <span className="format-card__gate-metric">{v.metric.replace(/_/g, ' ')}: {v.value}</span>
              <span className="format-card__gate-threshold">({v.value > v.threshold ? 'max' : 'min'} {v.threshold})</span>
              {v.severity !== 'pass' && <button className="format-card__fix-btn" onClick={(e) => { e.stopPropagation(); onAction('heal', slug); }}>Fix</button>}
            </div>
          ))}
        </div>
      )}

      {/* Destinations */}
      {destinations.length > 0 && (
        <div className="format-card__destinations">
          <div className="format-card__gates-title">Destinations</div>
          {destinations.map((d, i) => (
            <div key={i} className="format-card__dest-row">
              <span className="format-card__dest-channel">{d.channel}</span>
              {d.published ? (
                <>
                  <span className="format-card__dest-status format-card__dest-status--live">{'\u2713'} Published</span>
                  <a href={d.published.permalink} target="_blank" rel="noopener" className="format-card__dest-link" onClick={(e) => e.stopPropagation()}>View {'\u2192'}</a>
                </>
              ) : (
                <>
                  <span className="format-card__dest-status">{'\u25CB'} Ready</span>
                  {(d.channel === 'dev-to' || d.channel === 'hashnode') && (
                    <button className="btn btn-secondary btn--sm" onClick={(e) => { e.stopPropagation(); onAction(`preview:${d.channel}`, slug); }} title={`Preview ${d.channel === 'dev-to' ? 'Dev.to' : 'Hashnode'} version`}>Preview</button>
                  )}
                  <button className="btn btn-secondary btn--sm" onClick={(e) => { e.stopPropagation(); onAction(`publish:${d.channel}`, slug); }}>Publish</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
